import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Space, Tooltip } from 'antd';
import {
  ArrowLeftOutlined,
  ReloadOutlined,
  PrinterOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import { internalInvoices } from '../../api/internalAdminEndpoints';
import PageHeader from '../../components/PageHeader';
import ReportViewer from '../../components/invoices/ReportViewer';
import ZoomControls, { DEFAULT as DEFAULT_ZOOM } from '../../components/invoices/ZoomControls';
import { extractApiError, readTextError } from '../../utils/format';

// Stage 5N-9A — internal-admin Crystal-Reports-style viewer. Mirrors
// the tenant viewer page but uses the internal-admin axios client
// (separate JWT audience) and routes back to wherever the operator
// came from (browser back) rather than a fixed list. Shares the same
// sandboxed iframe + zoom logic via ReportViewer / ZoomControls.
export default function InternalInvoiceReportViewerPage() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const viewerRef = useRef(null);

  const [html, setHtml] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const body = await internalInvoices.viewReport(invoiceId);
      setHtml(body);
    } catch (err) {
      const apiErr = readTextError(err);
      setError(apiErr ? apiErr : { code: 'UNKNOWN', message: extractApiError(err) });
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => {
    load();
  }, [load]);

  const handlePrint = () => {
    if (viewerRef.current) viewerRef.current.print();
  };

  const viewerReady = !loading && !error && Boolean(html);

  return (
    <div>
      <PageHeader
        title="Invoice report"
        subtitle={
          <span className="text-ink-500">
            Internal admin read-only view. Generated from the same Jasper template
            shown to the tenant.
          </span>
        }
        icon={<FileTextOutlined />}
        actions={
          <Space>
            <Tooltip title="Back">
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
                Back
              </Button>
            </Tooltip>
            <Tooltip title="Reload report">
              <Button
                icon={<ReloadOutlined />}
                onClick={load}
                loading={loading}
              >
                Refresh
              </Button>
            </Tooltip>
            <ZoomControls
              zoom={zoom}
              onChange={setZoom}
              disabled={!viewerReady}
            />
            <Tooltip title="Print or save as PDF (browser dialog)">
              <Button
                type="primary"
                icon={<PrinterOutlined />}
                onClick={handlePrint}
                disabled={!viewerReady}
              >
                Print
              </Button>
            </Tooltip>
          </Space>
        }
      />

      {error ? (
        <Alert
          type="error"
          showIcon
          message={error.code || 'REPORT_RENDER_FAILED'}
          description={
            <Space direction="vertical">
              <span>{error.message || 'Could not load the invoice report.'}</span>
              <Button onClick={load} icon={<ReloadOutlined />}>
                Retry
              </Button>
              <Button type="link" onClick={() => navigate(-1)}>
                Back
              </Button>
            </Space>
          }
        />
      ) : (
        <ReportViewer
          ref={viewerRef}
          html={html}
          loading={loading}
          zoom={zoom}
        />
      )}
    </div>
  );
}
